import React, { ReactElement, useEffect, useState, ReactNode } from 'react'
import {
  SubMenuMainCell,
  SubMenuCell,
  SubMenuStyled,
  SubMenuCellText,
  MainCellText,
  SubMenuTrigger,
  SubMenuCollapsible,
} from './SubMenu.styled'
import { Tooltip } from '@clickcease/cc-client-components-lib'
import { useNavigate, useLocation } from 'react-router-dom'

export type SubMenuOptions = {
  title: string
  url: string
  icon?: ReactElement
}

export type SubMenuProps = {
  title: string
  options: Array<SubMenuOptions>
  image: any
  isActive: boolean
  collapsed: boolean
  disabled?: boolean
  disabledMessage?: string
  defaultNavigationUrl?: string
  setCollapsed: (collapsed: boolean) => void
  crossSell?: string
  navigateToDomainManagement?: string
}

export const SubMenu = ({
  title,
  options,
  image,
  isActive,
  collapsed,
  disabled = true,
  disabledMessage,
  defaultNavigationUrl,
  setCollapsed,
  crossSell,
  navigateToDomainManagement,
}: SubMenuProps) => {
  const [open, setOpen] = useState(true)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menuOpen = (open || isActive) && !collapsed

  useEffect(() => {
    // closer if user surf outside of sub menu domain
    setOpen(isActive)
  }, [isActive])

  const goToPage = (url: string) => {
    navigate(`../${url}`)
  }

  const handleSubMenuClick = (e: React.MouseEvent) => {
    if (disabled) return
    if (crossSell) {
      navigate(crossSell)
      return
    }
    if (navigateToDomainManagement) {
      navigate(navigateToDomainManagement, { state: { cameFromSideMenu: true } })
      return
    }
    e.stopPropagation()
    if (defaultNavigationUrl) goToPage(defaultNavigationUrl)
    if (collapsed) setCollapsed(false)
  }

  const renderOptions = (options: Array<SubMenuOptions>) => {
    if ((crossSell && crossSell?.length > 0) || navigateToDomainManagement) {
      return <></>
    } else {
      return options.map((option) => (
        <SubMenuCell
          key={option.url}
          cellActive={pathname.substring(1) === option.url}
          onClick={(e) => {
            e.stopPropagation()
            goToPage(option.url)
          }}
        >
          {option.icon}
          <SubMenuCellText>{option.title}</SubMenuCellText>
        </SubMenuCell>
      ))
    }
  }

  const DisabledTooltipWrapper = ({ children }: { children: ReactNode }): JSX.Element =>
    disabled && disabledMessage ? (
      <Tooltip tooltiptContent={disabledMessage} side="left">
        {children}
      </Tooltip>
    ) : (
      <>{children}</>
    )

  return (
    <DisabledTooltipWrapper>
      <SubMenuStyled
        open={menuOpen && !disabled}
        onOpenChange={setOpen}
        onClick={handleSubMenuClick}
      >
        <SubMenuTrigger asChild>
          <SubMenuMainCell
            disabled={disabled}
            isCrossSale={(crossSell && crossSell?.length > 0) || false}
            collapsed={collapsed}
            isOpen={!navigateToDomainManagement && menuOpen}
            isActive={isActive}
          >
            {image}
            <MainCellText collapsed={collapsed}>{title}</MainCellText>
          </SubMenuMainCell>
        </SubMenuTrigger>

        <SubMenuCollapsible>{renderOptions(options)}</SubMenuCollapsible>
      </SubMenuStyled>
    </DisabledTooltipWrapper>
  )
}
